import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BookOpen } from 'lucide-react';

import { ragApi } from '../../api/rag.js';
import { usePaginatedQuery } from '../../hooks/usePaginatedQuery.js';
import { DataTable } from '../../components/DataTable.jsx';
import { Pagination } from '../../components/ui/Pagination.jsx';
import { PageHeader } from '../../components/ui/PageHeader.jsx';
import { Input } from '../../components/ui/Input.jsx';
import { Badge } from '../../components/ui/Badge.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { Skeleton } from '../../components/ui/Skeleton.jsx';

const SOURCE_LABEL = {
  product: 'Prodotto',
  manual: 'Manuale',
  procedure: 'Procedura',
  article: 'Articolo',
  'owner-content': 'Contenuto titolare',
  misc: 'Vario'
};

/**
 * Read-only knowledge base for sellers. Reuses the RAG read endpoints (gated by
 * `rag:read`, which the seller role carries) to browse documents and read their
 * indexed content. A `?doc=<id>` query opens a document directly, used by the
 * global search deep-link; `?q=<text>` pre-filters the list.
 */
function DocumentViewer({ docId, onClose }) {
  const metaQuery = useQuery({
    queryKey: ['employee', 'kb', 'doc', docId],
    enabled: Boolean(docId),
    queryFn: () => ragApi.get(docId)
  });
  const chunksQuery = useQuery({
    queryKey: ['employee', 'kb', 'chunks', docId],
    enabled: Boolean(docId),
    queryFn: () => ragApi.listChunks(docId, { limit: 100, active: true })
  });

  const doc = metaQuery.data && (metaQuery.data.document || metaQuery.data);
  const chunks = (chunksQuery.data && chunksQuery.data.items) || [];

  return (
    <Modal
      open={Boolean(docId)}
      onClose={onClose}
      title={doc ? doc.title : 'Documento'}
      description={doc ? (SOURCE_LABEL[doc.sourceType] || doc.sourceType) : null}
      size="lg"
    >
      {(metaQuery.isLoading || chunksQuery.isLoading) ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
        </div>
      ) : chunks.length === 0 ? (
        <p className="text-sm text-ink-muted">Nessun contenuto indicizzato per questo documento.</p>
      ) : (
        <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto">
          {chunks.map((c) => (
            <p key={c._id} className="whitespace-pre-wrap text-sm leading-relaxed text-ink">
              {c.text}
            </p>
          ))}
        </div>
      )}
    </Modal>
  );
}

export function KnowledgePage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const initialFilters = useMemo(() => {
    const f = {};
    const q = searchParams.get('q');
    if (q) f.q = q;
    return f;
  // Read once on mount; later edits go through the in-page filter state.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { items, isLoading, pagination, setPage, filters, setFilters } = usePaginatedQuery({
    key: ['employee', 'kb', 'list'],
    fetcher: ragApi.list,
    initialFilters
  });

  // The open document is driven entirely by the `?doc` query param, so a global
  // search deep-link and an in-page row click share one source of truth.
  const openDocId = searchParams.get('doc') || null;
  const openDoc = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set('doc', id);
    setSearchParams(next);
  };
  const closeViewer = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('doc');
    setSearchParams(next, { replace: true });
  };

  const columns = [
    {
      key: 'title',
      header: 'Documento',
      render: (r) => (
        <button type="button" className="text-left text-ink hover:underline" onClick={() => openDoc(r._id)}>
          {r.title}
        </button>
      )
    },
    {
      key: 'sourceType',
      header: 'Tipo',
      render: (r) => <Badge tone="neutral">{SOURCE_LABEL[r.sourceType] || r.sourceType}</Badge>
    },
    {
      key: 'chunkCount',
      header: 'Sezioni',
      align: 'right',
      render: (r) => <span className="text-ink-muted">{r.chunkCount || 0}</span>
    }
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Knowledge base"
        description="Documenti e materiali ORME consultabili. Apri un documento per leggerne il contenuto."
      />

      <div className="flex flex-wrap items-end gap-3">
        <Input
          className="w-72"
          label="Cerca"
          placeholder="titolo, argomento..."
          value={filters.q || ''}
          onChange={(e) => setFilters({ q: e.target.value || undefined })}
        />
      </div>

      <DataTable
        columns={columns}
        rows={items}
        loading={isLoading}
        empty={{ title: 'Nessun documento', description: 'Non risultano documenti nella knowledge base.', icon: BookOpen }}
      />

      <Pagination pagination={pagination} onPageChange={setPage} />

      <DocumentViewer docId={openDocId} onClose={closeViewer} />
    </div>
  );
}
